/**
 * Canonical printing of expressions (W1-10c core-expr), used by `Expr.format` to show a tool's equivalent
 * expression (Distribute: "= lerp(-400, 400, ease(6, u)) * alt(1, -1)").
 *
 * Canonical form: binary operators surrounded by single spaces except `^` (`x^2`), `, ` between arguments,
 * the fewest parentheses that keep the tree (a right operand of the same precedence keeps its parentheses:
 * `a - (b - c)`), known names lower-cased (`PI` -> `pi`, `$t$` -> `t`, `π` -> `pi`), numbers in JavaScript's
 * shortest form with lower-case units (`.5` -> `0.5`, `45DEG` -> `45deg`, `12 px` -> `12px`).
 * Printing a parsed tree and parsing the result gives the same tree.
 */

import { findFunction } from './functions';
import { unitOf } from './vars';
import { ALL_VAR_NAMES, canonicalName, findConst } from './vars';
import type { RawNode } from './parser';

const PREC_ADD = 1;
const PREC_MUL = 2;
const PREC_UNARY = 3;
const PREC_POW = 4;
const PREC_ATOM = 5;

function precOf(n: RawNode): number {
  switch (n.t) {
    case 'bin':
      return n.op === '+' || n.op === '-' ? PREC_ADD : n.op === '^' ? PREC_POW : PREC_MUL;
    case 'neg':
      return PREC_UNARY;
    case 'num':
    case 'id':
    case 'str':
    case 'call':
      return PREC_ATOM;
  }
}

function wrap(s: string, parens: boolean): string {
  return parens ? `(${s})` : s;
}

function printName(name: string): string {
  const canon = canonicalName(name);
  if (ALL_VAR_NAMES.has(canon) || findConst(canon) !== undefined || findFunction(canon) !== undefined) return canon;
  return name;
}

function printNumber(value: number, unit: string | null): string {
  const num = String(value);
  if (unit === null) return num;
  const known = unitOf(unit);
  return `${num}${known ?? unit}`;
}

function printString(value: string): string {
  return value.includes('"') ? `'${value}'` : `"${value}"`;
}

/** Prints a raw expression tree canonically. */
export function printRaw(n: RawNode): string {
  switch (n.t) {
    case 'num':
      return printNumber(n.value, n.unit);
    case 'id':
      return printName(n.name);
    case 'str':
      return printString(n.value);
    case 'call':
      return `${printName(n.name)}(${n.args.map(printRaw).join(', ')})`;
    case 'neg':
      return `-${wrap(printRaw(n.arg), precOf(n.arg) < PREC_UNARY)}`;
    case 'bin': {
      const p = precOf(n);
      const pa = precOf(n.a);
      const pb = precOf(n.b);
      if (n.op === '^') {
        // Right-associative: (a^b)^c and (-a)^b need parentheses on the left; the right side takes unary and ^.
        const left = wrap(printRaw(n.a), pa <= p);
        const right = wrap(printRaw(n.b), pb < PREC_UNARY);
        return `${left}^${right}`;
      }
      const left = wrap(printRaw(n.a), pa < p);
      const right = wrap(printRaw(n.b), pb <= p);
      return `${left} ${n.op} ${right}`;
    }
  }
}
